import { ReactNode, useState, useRef, useCallback } from 'react';
import { AnimatePresence, motion, useMotionValue, animate } from 'framer-motion';
import { CaretLeft, CaretRight, FrameCorners } from '@phosphor-icons/react';
import PlaybackBar from '../toolbar/PlaybackBar';
import TimelinePanel from '../timeline/TimelinePanel';
import { useToolStore } from '../../stores/toolStore';

interface EditorLayoutProps {
  leftSidebar: ReactNode;
  canvas: ReactNode;
  rightSidebar: ReactNode;
  header: ReactNode;
}

const MIN_W = 160;
const MAX_W = 520;
const DEFAULT_LEFT_W = 264;
const DEFAULT_RIGHT_W = 304;

function PanelEdge({
  side,
  isCollapsed,
  onToggle,
  onResizeStart,
}: {
  side: 'left' | 'right';
  isCollapsed: boolean;
  onToggle: () => void;
  onResizeStart: (e: React.MouseEvent) => void;
}) {
  const isLeft = side === 'left';
  const ChevronIcon = isLeft
    ? isCollapsed ? CaretRight : CaretLeft
    : isCollapsed ? CaretLeft : CaretRight;

  return (
    <div className="hidden xl:flex items-stretch shrink-0 select-none" data-panel-edge={side}>
      {/* Resize strip — left side of left panel edge */}
      {isLeft && !isCollapsed && (
        <div
          onMouseDown={onResizeStart}
          className="w-[4px] cursor-col-resize group relative"
          data-testid="left-resize-handle"
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border-subtle group-hover:bg-accent/40 transition-colors" />
        </div>
      )}

      {/* Collapse toggle strip */}
      <button
        onClick={onToggle}
        className="relative w-5 flex items-center justify-center group hover:bg-bg-hover/30 transition-colors duration-fast"
        title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
        data-testid={`${side}-panel-toggle`}
      >
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border-subtle group-hover:bg-accent/20 transition-colors" />
        <div className="relative z-10 w-[18px] h-6 rounded-sm bg-bg-elevated border border-border-subtle flex items-center justify-center group-hover:border-accent/30 transition-colors duration-fast">
          <ChevronIcon size={9} weight="bold" className="text-text-tertiary group-hover:text-accent transition-colors" />
        </div>
      </button>

      {/* Resize strip — right side of right panel edge */}
      {!isLeft && !isCollapsed && (
        <div
          onMouseDown={onResizeStart}
          className="w-[4px] cursor-col-resize group relative"
          data-testid="right-resize-handle"
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border-subtle group-hover:bg-accent/40 transition-colors" />
        </div>
      )}
    </div>
  );
}

export default function EditorLayout({ leftSidebar, canvas, rightSidebar, header }: EditorLayoutProps) {
  const isPresentationMode = useToolStore((s) => s.isPresentationMode);
  const exitPresentationMode = useToolStore((s) => s.exitPresentationMode);

  // Mobile drawer state
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  // Desktop collapse state
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  // Panel widths (used for inner content sizing)
  const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_W);
  const [rightWidth, setRightWidth] = useState(DEFAULT_RIGHT_W);
  const leftWidthRef = useRef(DEFAULT_LEFT_W);
  const rightWidthRef = useRef(DEFAULT_RIGHT_W);

  // Motion values for smooth animated width (bypass re-renders during resize)
  const leftWidthMV = useMotionValue(DEFAULT_LEFT_W);
  const rightWidthMV = useMotionValue(DEFAULT_RIGHT_W);

  const toggleLeftCollapse = useCallback(() => {
    setLeftCollapsed((prev) => {
      const next = !prev;
      animate(leftWidthMV, next ? 0 : leftWidthRef.current, {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
      });
      return next;
    });
  }, [leftWidthMV]);

  const toggleRightCollapse = useCallback(() => {
    setRightCollapsed((prev) => {
      const next = !prev;
      animate(rightWidthMV, next ? 0 : rightWidthRef.current, {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
      });
      return next;
    });
  }, [rightWidthMV]);

  const startLeftResize = useCallback(
    (e: React.MouseEvent) => {
      if (leftCollapsed) return;
      e.preventDefault();
      const startX = e.clientX;
      const startW = leftWidthRef.current;
      const onMove = (ev: MouseEvent) => {
        const w = Math.max(MIN_W, Math.min(MAX_W, startW + ev.clientX - startX));
        leftWidthRef.current = w;
        leftWidthMV.set(w);
        setLeftWidth(w);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [leftCollapsed, leftWidthMV]
  );

  const startRightResize = useCallback(
    (e: React.MouseEvent) => {
      if (rightCollapsed) return;
      e.preventDefault();
      const startX = e.clientX;
      const startW = rightWidthRef.current;
      const onMove = (ev: MouseEvent) => {
        const w = Math.max(MIN_W, Math.min(MAX_W, startW - (ev.clientX - startX)));
        rightWidthRef.current = w;
        rightWidthMV.set(w);
        setRightWidth(w);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [rightCollapsed, rightWidthMV]
  );

  // Presentation mode — full-screen canvas, no chrome
  if (isPresentationMode) {
    return (
      <div className="flex flex-col h-screen bg-bg-deepest overflow-hidden">
        <div className="flex-1 relative overflow-hidden">{canvas}</div>
        {/* Exit badge */}
        <button
          onClick={exitPresentationMode}
          className="absolute top-3 right-3 z-50 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-bg-elevated/80 backdrop-blur-sm border border-border text-text-tertiary hover:text-text-primary hover:bg-bg-elevated text-xs font-mono transition-all"
          title="Exit presentation mode (Esc)"
        >
          <FrameCorners size={13} />
          Esc to exit
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-bg-deepest overflow-hidden">
      {/* Header */}
      <div className="shrink-0">{header}</div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden relative">

        {/* ── LEFT SIDEBAR ── desktop only */}
        <motion.div
          style={{ width: leftWidthMV, overflow: 'hidden' }}
          className="hidden xl:flex shrink-0 flex-col bg-bg-surface"
          data-testid="left-sidebar"
        >
          {/* Fixed-width inner prevents content from squashing during animation */}
          <div
            style={{ width: leftWidth, minWidth: leftWidth }}
            className="h-full flex flex-col border-r border-border-subtle"
          >
            {leftSidebar}
          </div>
        </motion.div>

        {/* Left panel edge (resize handle + collapse toggle) */}
        <PanelEdge
          side="left"
          isCollapsed={leftCollapsed}
          onToggle={toggleLeftCollapse}
          onResizeStart={startLeftResize}
        />

        {/* Left DRAWER — mobile/tablet */}
        <AnimatePresence>
          {leftOpen && (
            <>
              <motion.div
                key="left-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="xl:hidden fixed inset-0 z-sidebar bg-black/40 backdrop-blur-sm"
                onClick={() => setLeftOpen(false)}
              />
              <motion.div
                key="left-drawer"
                initial={{ x: -280, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -280, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] as const }}
                className="xl:hidden fixed left-0 top-[52px] bottom-[48px] z-sidebar w-[280px] bg-bg-surface border-r border-border-subtle flex flex-col overflow-hidden"
                data-testid="left-drawer"
              >
                {leftSidebar}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ── CANVAS ── */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 relative overflow-hidden min-h-0">{canvas}</div>
          {/* Timeline panel — above playbar */}
          <TimelinePanel />
          <div className="shrink-0 h-12 relative z-toolbar">
            <PlaybackBar
              onToggleLeft={() => setLeftOpen((v) => !v)}
              onToggleRight={() => setRightOpen((v) => !v)}
              leftOpen={leftOpen}
              rightOpen={rightOpen}
            />
          </div>
        </div>

        {/* Right panel edge (collapse toggle + resize handle) */}
        <PanelEdge
          side="right"
          isCollapsed={rightCollapsed}
          onToggle={toggleRightCollapse}
          onResizeStart={startRightResize}
        />

        {/* ── RIGHT SIDEBAR ── desktop only */}
        <motion.div
          style={{ width: rightWidthMV, overflow: 'hidden' }}
          className="hidden xl:flex shrink-0 flex-col bg-bg-surface"
          data-testid="right-sidebar"
        >
          <div
            style={{ width: rightWidth, minWidth: rightWidth }}
            className="h-full flex flex-col overflow-y-auto overflow-x-hidden border-l border-border-subtle"
          >
            {rightSidebar}
          </div>
        </motion.div>

        {/* Right DRAWER — mobile/tablet */}
        <AnimatePresence>
          {rightOpen && (
            <>
              <motion.div
                key="right-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="xl:hidden fixed inset-0 z-sidebar bg-black/40 backdrop-blur-sm"
                onClick={() => setRightOpen(false)}
              />
              <motion.div
                key="right-drawer"
                initial={{ x: 320, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 320, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] as const }}
                className="xl:hidden fixed right-0 top-[52px] bottom-[48px] z-sidebar w-[320px] bg-bg-surface border-l border-border-subtle flex flex-col overflow-y-auto"
                data-testid="right-drawer"
              >
                {rightSidebar}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
